import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import {
    getRewards,
    getSurveyRewards,
    setupSurveyRewards,
    IRewardItem,
} from 'services/reward.service';
import { handleError, createStyleSheet } from 'utils/helpers';
import { useParams } from 'react-router-dom';

interface RewardFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface IRewardSelection {
    reward: IRewardItem;
    checked: boolean;
    quantity: number;
    inputValue: string;
}

const RewardForm: React.FC<RewardFormProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const { id: surveyId } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selections, setSelections] = useState<IRewardSelection[]>([]);

    // Fetch danh sách rewards và survey rewards khi mở modal
    useEffect(() => {
        if (open && surveyId) {
            fetchData();
        }
    }, [open, surveyId]);

    const fetchData = async () => {
        if (!surveyId) return;

        setLoading(true);
        try {
            const [rewardsRes, surveyRewardsRes] = await Promise.all([
                getRewards(),
                getSurveyRewards(Number(surveyId)),
            ]);

            if (rewardsRes.success && rewardsRes.data) {
                const allRewards = rewardsRes.data.rewards;

                // Chỉ lấy rewards có status là "active"
                const activeRewards = allRewards.filter(
                    (reward) => reward.attributes.status === 'active'
                );

                const existingSurveyRewards =
                    surveyRewardsRes.success && surveyRewardsRes.data
                        ? surveyRewardsRes.data.survey_rewards
                        : [];

                const initialSelections: IRewardSelection[] = activeRewards.map(
                    (reward) => {
                        const existingSetup = existingSurveyRewards.find(
                            (sr) =>
                                sr.attributes.reward.attributes.id ===
                                reward.attributes.id
                        );

                        const qty = existingSetup?.attributes.quantity ?? 1;
                        return {
                            reward,
                            checked: !!existingSetup,
                            quantity: qty,
                            inputValue: String(qty),
                        };
                    }
                );

                setSelections(initialSelections);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    // ...existing code...

    // Handle checkbox change
    const handleCheckChange = (rewardId: number, checked: boolean) => {
        setSelections((prev) =>
            prev.map((item) =>
                item.reward.attributes.id === rewardId
                    ? {
                          ...item,
                          checked,
                          // Reset về 1 khi check
                          quantity: checked
                              ? item.quantity < 1
                                  ? 1
                                  : item.quantity
                              : item.quantity,
                          inputValue: checked
                              ? item.quantity < 1
                                  ? '1'
                                  : item.inputValue
                              : item.inputValue,
                      }
                    : item
            )
        );
    };

    // Handle quantity change - chỉ cập nhật inputValue
    const handleQuantityChange = (rewardId: number, value: string) => {
        // Chỉ cho phép nhập số hoặc rỗng
        if (value !== '' && !/^\d+$/.test(value)) return;

        const numValue = value === '' ? 0 : parseInt(value, 10);

        setSelections((prev) =>
            prev.map((item) =>
                item.reward.attributes.id === rewardId
                    ? {
                          ...item,
                          inputValue: value,
                          quantity: numValue,
                      }
                    : item
            )
        );
    };

    // Handle blur - validate và format lại
    const handleQuantityBlur = (rewardId: number) => {
        setSelections((prev) =>
            prev.map((item) => {
                if (item.reward.attributes.id !== rewardId) return item;
                // Nếu quantity < 1, set về 1
                const qty = item.quantity < 1 ? 1 : item.quantity;
                return {
                    ...item,
                    quantity: qty,
                    inputValue: String(qty),
                };
            })
        );
    };

    // Handle save
    const handleSave = async () => {
        if (!surveyId) return;

        const selectedRewards = selections.filter((item) => item.checked);

        if (selectedRewards.length < 1) {
            toast.warning('Vui lòng chọn ít nhất 1 phần thưởng');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                rewards: selectedRewards.map((item) => ({
                    reward_id: item.reward.attributes.id,
                    quantity: item.quantity < 1 ? 1 : item.quantity,
                })),
            };

            const response = await setupSurveyRewards(
                Number(surveyId),
                payload
            );

            if (response.success) {
                toast.success(
                    response.message || 'Thiết lập phần thưởng thành công'
                );
                onSuccess?.();
                onClose();
            }
        } catch (error) {
            handleError(error);
        } finally {
            setSaving(false);
        }
    };

    // Handle close
    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    // Computed values
    const selectedCount = selections.filter((s) => s.checked).length;
    const totalQuantity = selections
        .filter((s) => s.checked)
        .reduce((sum, s) => sum + (s.quantity < 1 ? 1 : s.quantity), 0);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6" fontWeight="bold">
                        Thiết lập phần thưởng
                    </Typography>
                    <IconButton onClick={handleClose} disabled={saving}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={300}
                    >
                        <CircularProgress />
                    </Box>
                ) : selections.length === 0 ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={300}
                    >
                        <Typography color="text.secondary">
                            Chưa có phần thưởng nào. Vui lòng tạo phần thưởng
                            trước.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            padding="checkbox"
                                            width={50}
                                        >
                                            Chọn
                                        </TableCell>
                                        <TableCell width={120}>
                                            Hình ảnh
                                        </TableCell>
                                        <TableCell>Tên phần thưởng</TableCell>
                                        <TableCell>Chi tiết</TableCell>
                                        <TableCell width={120} align="center">
                                            Số lượng
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selections.map((item) => (
                                        <TableRow
                                            key={item.reward.id}
                                            hover
                                            sx={{
                                                backgroundColor: item.checked
                                                    ? 'action.selected'
                                                    : 'inherit',
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={item.checked}
                                                    onChange={(e) =>
                                                        handleCheckChange(
                                                            item.reward
                                                                .attributes.id,
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {item.reward.attributes.image
                                                    .url ? (
                                                    <Box
                                                        component="img"
                                                        src={
                                                            item.reward
                                                                .attributes
                                                                .image.url
                                                        }
                                                        alt={
                                                            item.reward
                                                                .attributes.name
                                                        }
                                                        sx={{
                                                            width: 50,
                                                            height: 50,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: 50,
                                                            height: 50,
                                                            backgroundColor:
                                                                '#f5f5f5',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            N/A
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight={500}>
                                                    {
                                                        item.reward.attributes
                                                            .name
                                                    }
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {item.reward.attributes
                                                        .detail || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <TextField
                                                    type="text"
                                                    size="small"
                                                    value={item.inputValue}
                                                    onBlur={() =>
                                                        handleQuantityBlur(
                                                            item.reward
                                                                .attributes.id
                                                        )
                                                    }
                                                    onChange={(e) =>
                                                        handleQuantityChange(
                                                            item.reward
                                                                .attributes.id,
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={!item.checked}
                                                    inputProps={{
                                                        style: {
                                                            textAlign: 'center',
                                                            backgroundColor:
                                                                item.checked
                                                                    ? '#fff'
                                                                    : '#f5f5f5',
                                                        },
                                                    }}
                                                    sx={{ width: 80 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Summary */}
                        <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Số phần thưởng đã chọn:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {selectedCount} / {selections.length}
                                </Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                mt={1}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Tổng số lượng:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {totalQuantity}
                                </Typography>
                            </Stack>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    sx={{
                        ...styles.button,
                        minWidth: '155px',
                        borderWidth: '2px',
                    }}
                    variant="outlined"
                    onClick={handleClose}
                    disabled={saving}
                >
                    Hủy
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || saving}
                    sx={{
                        ...styles.button,
                        minWidth: '155px',
                        borderWidth: '2px',
                    }}
                >
                    {saving ? 'Đang lưu...' : 'Lưu thiết lập'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const styles = createStyleSheet({
    button: {
        minWidth: '160px',
        minHeight: '36px',
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 700,
    },
});
export default RewardForm;
